import React, { createContext, useEffect } from 'react';
import { connect } from 'react-redux';
import { gql, useMutation } from '@apollo/client';
import BrowserStore from 'store2';
import { useCookies } from 'react-cookie';
import { authLogout, getAuthenticatedUserData, setCommentUser, setGqlToken } from '../actions/auth';
import useCrossTabState from '../hooks/useCrossTabState';

const AuthContext = createContext(null);

const AUTHENTICATE_GQL = gql`
  mutation Authorize($token: String!) {
    authorize(
      input: {token: $token}
    ) {
      token
      user {
        id
        uuid
        username
        display_name
        photo
        banned_permanent
        banned_at
        blacklisted
        elevated_user
        roles {
          id
          name
        }
        boards {
          id
          uuid
          name
        }
        created_at
        updated_at
      }
    }
  }
`;
const AuthContextProvider = ({ children, commentToken, setCommentUserInfo, getAuthUserData, logout, isAuthenticated, loginSyncState }) => {
  const [cookies, setCookie] = useCookies(['gql_token', 'comment_user']);
  const [mutateFunction] = useMutation(AUTHENTICATE_GQL);
  const [userLoginSyncState, setUserLoginSyncState] = useCrossTabState('userLogin', false);
  const [authenticatedSyncState, setAuthenticatedSyncState] = useCrossTabState('authenticated', loginSyncState);

  useEffect(() => {
    if (userLoginSyncState && !isAuthenticated) {
      getAuthUserData();
    } else if (!userLoginSyncState && isAuthenticated && authenticatedSyncState === -1) {
      logout();
    }
  }, [userLoginSyncState]);

  useEffect(() => {
    setAuthenticatedSyncState(loginSyncState);
  }, [loginSyncState]);

  const graphqlAuth = async () => {
    if (commentToken && !cookies.gql_token) {
      const results = await mutateFunction({ variables: { token: commentToken } });
      const { token, user } = results?.data?.authorize;
      const jwtPieces = token.split('.');
      const tokenData = JSON.parse(atob(jwtPieces[1]));
      const expires = new Date(tokenData.exp * 1000);
      setCookie('gql_token', token, { expires });
      setCookie('comment_user', user, { expires });
      setCommentUserInfo(token, user);
      BrowserStore.set('gql_token', results?.data?.authorize?.token);
      setUserLoginSyncState(true);
    } else if (commentToken) {
      setCommentUserInfo(cookies.gql_token, cookies.comment_user);
      setUserLoginSyncState(!!cookies.gql_token);
    } else {
      setUserLoginSyncState(false);
      setCommentUserInfo(null, null);
    }
  };
  useEffect(() => {
    graphqlAuth();
  }, [commentToken]);
  return (
    <AuthContext.Provider value={{}}>
      {children}
    </AuthContext.Provider>
  );
};

const mapStateToProps = (state) => {
  return {
    commentToken: state.auth.commentToken,
    isAuthenticated: state.auth.isAuthenticated,
    loginSyncState: state.auth.loginSyncState,
  };
};

const mapDispatchToProps = (dispatch) => {
  return {
    setCommentUserInfo: (token, user) => {
      dispatch(setGqlToken(token));
      dispatch(setCommentUser(user));
    },
    getAuthUserData: () => dispatch(getAuthenticatedUserData()),
    logout: () => dispatch(authLogout()),
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(AuthContextProvider);
